import { useCallback, useEffect, useMemo, useState } from 'react';
import gtLogo from 'assets/images/gt-logo.png';
import {
  getChecklistFileEntries,
  getChecklistInfo,
  getSanitizedChecklistText,
  isChecklistItemInvalid,
} from '../checklListUtils';
import { LoadingImages, PageContainer, ReportHeader } from './styles';

const PRINT_STORAGE_KEY = 'gt-checklist-avulso-print';
const imageExtensions = ['jpeg', 'jpg', 'png', 'gif', 'webp'];

interface ImagesLoadedConfig {
  imagesCount: number;
  fetchedLinks?: boolean;
  loaded: number;
}

export const ChecklistAvulsoPrintingPage: React.FC = () => {
  const [imagesLoadedConfig, setImagesLoadedConfig] = useState<ImagesLoadedConfig>({
    loaded: 0,
    imagesCount: 0,
  });

  const data = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(PRINT_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const fileItens = useMemo(() => {
    return getChecklistFileEntries(data)
      .map((item: any) => {
        const link = item?.file_url || item?.fileUrl;
        if (!link) {
          return null;
        }

        const raw = link.split('?')[0];
        const extension = raw.includes('.') ? raw.split('.').pop()?.toLowerCase() || '' : '';

        return { ...item, link, extension };
      })
      .filter(Boolean) as Array<{ link: string; extension: string; [key: string]: any }>;
  }, [data]);

  const imageItens = useMemo(
    () => fileItens.filter((item) => imageExtensions.includes(item.extension)),
    [fileItens]
  );

  const onAllImagesLoaded = useCallback(() => {
    const { imagesCount, loaded, fetchedLinks } = imagesLoadedConfig;
    if (fetchedLinks && imagesCount === loaded) {
      window.print();
    }
  }, [imagesLoadedConfig]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setImagesLoadedConfig({
      loaded: 0,
      imagesCount: imageItens.length,
      fetchedLinks: true,
    });
  }, [data, imageItens.length]);

  useEffect(() => {
    onAllImagesLoaded();
  }, [onAllImagesLoaded]);

  if (!data) {
    return <div style={{ padding: 24 }}>Nenhum checklist selecionado para impressão.</div>;
  }

  return (
    <PageContainer>
      <ReportHeader>
        <div className="logo-wrapper">
          <img src={gtLogo} alt="GT" />
        </div>
        <div className="header-text">
          <span>Checklist Avulso</span>
          {data?.id && <span>Ordem: {data.id}</span>}
        </div>
      </ReportHeader>

      {imagesLoadedConfig.imagesCount > 0 &&
        imagesLoadedConfig.loaded < imagesLoadedConfig.imagesCount && (
          <div className="mb-8 flex flex-row items-center gap-3">
            <LoadingImages />
            <strong className="text-sm">Carregando imagens</strong>
          </div>
        )}

      <div className="grid grid-cols-[max-content_max-content_max-content_max-content] gap-x-5 gap-y-2 mb-3 pb-3 border-b border-b-gray-500">
        <ul>
          <li>
            <strong>Checklist</strong>
            <p>{getChecklistInfo(data, 'tipo') || '--'}</p>
          </li>
        </ul>
        <ul>
          <li>
            <strong>Usuario</strong>
            <p>{getChecklistInfo(data, 'user') || '--'}</p>
          </li>
        </ul>
        <ul>
          <li>
            <strong>Status</strong>
            <p>{getChecklistInfo(data, 'status') || '--'}</p>
          </li>
        </ul>
        <ul>
          <li>
            <strong>Feito em</strong>
            <p>{getChecklistInfo(data, 'conclusão') || '--'}</p>
          </li>
        </ul>
      </div>

      <div className="grid grid-cols-[max-content_max-content_max-content] gap-x-5 gap-y-2 mb-3 pb-3">
        <ul>
          <li>
            <strong>Agendamento</strong>
            <p>{getChecklistInfo(data, 'agendamento') || '--'}</p>
          </li>
        </ul>
        <ul>
          <li>
            <strong>Inicio</strong>
            <p>{data?.started_at ? getChecklistInfo(data, 'inicio') : '--'}</p>
          </li>
        </ul>
        <ul>
          <li>
            <strong>Tempo</strong>
            <p>{getChecklistInfo(data, 'tempo') || '--'}</p>
          </li>
        </ul>
      </div>

      <ul>
        {(() => {
          const checklistItens = Array.isArray(data?.itens) ? data.itens : [];
          const textOnlyItens = checklistItens.filter((item: any) => {
            const link = item?.file_url || item?.fileUrl;
            return !String(link || '').includes('http://') && !String(link || '').includes('https://');
          });

          return [...textOnlyItens, ...fileItens];
        })().map((item: any) => {
          const link = item?.link || item?.file_url || item?.fileUrl;
          const raw = link ? link.split('?')[0] : '';
          const extension = raw.includes('.') ? raw.split('.').pop()?.toLowerCase() || '' : '';
          const isImage = imageExtensions.includes(extension);
          const isPdf = extension === 'pdf';
          const isInvalid = isChecklistItemInvalid(item);
          const textClassName = isInvalid ? 'text-red-600' : '';
          const questionText = getSanitizedChecklistText(item.question_text);
          const answerText = getSanitizedChecklistText(item.answer);

          return (
            <li key={item.id} className="border-b border-b-slate-300 mb-2">
              <strong className={textClassName}>{questionText}</strong>
              {link ? (
                <div className="p-1 flex items-center max-w-80 max-h-80 bg-gray-100 rounded-sm">
                  {isImage && (
                    <img
                      src={link}
                      className="max-h-72 w-auto object-contain"
                      onLoad={() =>
                        setImagesLoadedConfig((prev) => ({
                          ...prev,
                          loaded: prev.loaded + 1,
                        }))
                      }
                    />
                  )}
                  {isPdf && (
                    <embed
                      src={link}
                      type="application/pdf"
                      title={`pdf-${item.id}`}
                      className="w-full h-full rounded-lg border"
                    />
                  )}
                  {!isImage && !isPdf && <p className={`pl-1 ${textClassName}`}>{answerText}</p>}
                </div>
              ) : (
                <p className={`pl-1 ${textClassName}`}>{answerText}</p>
              )}
            </li>
          );
        })}
      </ul>
    </PageContainer>
  );
};
